import format from '@/module/util/format'

const MEAL = ['breakfast', 'lunch', 'dinner', 'beverage', 'snack']
const SIZE = ['small', 'medium', 'large']
const HEALTH = ['not healthy', 'somewhat healthy', 'very healthy']

export const NUTRIENTS = {
	energy: {
		name: 'Energy',
		code: 1008
	},
	protein: {
		name: 'Protein',
		code: 1003
	},
	fat: {
		name: 'Fat',
		code: 1004
	},
	carbohydrates: {
		name: 'Carbohydrates',
		code: 1005
	},
	sugar: {
		name: 'Sugar',
		code: 2000
	}
}

export const MACRONUTRIENTS = ['energy', 'carbohydrates', 'fat', 'protein']

export default {
	props: {
		food: {
			type: Array,
			required: true
		}
	},

	methods: {
		...format,
		binWidth(bin, total) {
			if (total == 0) return 0
			return (bin * 100.0 / total) + '%'
		},
		binLabel(bin, total) {
			if (total == 0) return 0
			return Math.round(bin * 100.0 / total) + '%'
		},
		mealImage(meal) {
			return require('./image/' + this.formatMeal(meal) + '.png')
		},
		formatMeal(meal) {
			return MEAL[meal]
		},
		formatSize(size) {
			return SIZE[size]
		},
		formatHealth(health) {
			return HEALTH[health]
		}
	},

	computed: {
		sortedFood() {
			return this.food.slice().sort((a, b) => (b.time - a.time))
		},
		healthBins() {
			var bins = [0, 0, 0]
			this.food.forEach((food) => {
				if (food.health != null)
					bins[food.health]++
			})
			return bins
			// return [0, 100, 0]
		},
		sizeBins() {
			var bins = [0, 0, 0]
			this.food.forEach((food) => {
				if (food.size != null)
					bins[food.size]++
			})
			return bins
		},
		healthTotal() {
			return this.healthBins.reduce((a, b) => (a + b), 0)
		},
		sizeTotal() {
			return this.sizeBins.reduce((a, b) => (a + b), 0)
		},
		/**
		 * Filters food that has nutrient information and is not more than 30 days old
		 *
		 * @returns {[*]}
		 */
		filteredNutrientFood() {
			let oneMonthBefore =  new Date()
			oneMonthBefore.setHours(24,0,0)
			oneMonthBefore.setDate(oneMonthBefore.getDate() - 30)
			return this.food.filter((food) => food.hasOwnProperty('nutrient') && Array.isArray(food.nutrient) && food.time > oneMonthBefore)
		},
		/**
		 * An object of datewise energy consumed for the past 30 days
		 * 
		 * @returns {Object}
		 */
		energyMap() {
			return this.filteredNutrientFood.reduce((energyAccum, food) => {
				const energy = food.nutrient.find((nutrient) => nutrient.id === NUTRIENTS.energy.code)
				// Check if we have energy in nutrient and if energy has value inside it
				if(energy && energy.value) {
					const foodDate = this.formatDate(food.time, true, true)
					if(!energyAccum.hasOwnProperty(foodDate))
						energyAccum[foodDate] = {
							time: new Date(foodDate),
							value: 0
						}
					if(food.quick_add)
						energyAccum[foodDate].value += energy.value * (food.serving_size || 1)
					else
						energyAccum[foodDate].value += energy.value / 100 * (food.base_value || 100) * (food.serving_size || 1)
				}
				return energyAccum
			}, {})
		},
		/**
		 * Sorted `energyMap` by date
		 *
		 * @returns
		 */
		sortedEnergyArr() {
			return Object.values(this.energyMap).sort((energyA, energyB) => energyA.date - energyB.date)
		},
		/**
		 * Average energy consumed in the past 30 days for days that have any energy recording
		 *
		 * @returns {Number}
		 */
		averageEnergy() {
			return Math.round(this.sortedEnergyArr.reduce((acc, curr) => (acc = acc + curr.value), 0) / this.sortedEnergyArr.length)
		},
		/**
		 * Calculates daily macronutrients (energy, fat, protein, carbs) for the last 30 days 
		 *
		 * @returns {[*]}
		 */
		sortedMacronutrientArr() {
			return Object.values(this.filteredNutrientFood.reduce((nutrientAccum, food) => {
				// Getting formatted date for object key
				try {
					const foodDate = this.formatDate(food.time, true, true)
					// Adding entries in the nutrient map fo each of the macronutients
					MACRONUTRIENTS.forEach((nutrientKey) => {
						const nutrientObj = food.nutrient.find((nutrient) => nutrient.id === NUTRIENTS[nutrientKey].code)
						if(nutrientObj && nutrientObj.value) {
							if(!nutrientAccum.hasOwnProperty(foodDate))
								nutrientAccum[foodDate] = {
									time: new Date(foodDate)
								}
							nutrientAccum[foodDate][nutrientKey] = nutrientAccum[foodDate].hasOwnProperty(nutrientKey) ? nutrientAccum[foodDate][nutrientKey] : 0
							if(food.quick_add)
								nutrientAccum[foodDate][nutrientKey] += nutrientObj.value * (food.serving_size || 1)
							else
								nutrientAccum[foodDate][nutrientKey] += nutrientObj.value / 100 * (food.base_value || 100) * (food.serving_size || 1)
						}
					})
					return nutrientAccum
				} catch (error) {
					console.error(error)
					return nutrientAccum
				}
			}, {})
			)
		},
		/**
		 * Calulates average macronutrient in the last 30 days
		 *
		 */
		avgMacronutrient() {
			const nutrientMap = this.filteredNutrientFood.reduce((nutrientAccum, food) => {
				// Getting formatted date for object key
				try {
					const foodDate = this.formatDate(food.time, true, true)
					// Adding entriesn in the nutrient map fo each od the macronutients
					MACRONUTRIENTS.forEach((nutrientKey) => {
						const nutrientObj = food.nutrient.find((nutrient) => nutrient.id === NUTRIENTS[nutrientKey].code)
						if(nutrientObj && nutrientObj.value) {
							if(!nutrientAccum.hasOwnProperty(foodDate))
								nutrientAccum[foodDate] = {}
							nutrientAccum[foodDate][nutrientKey] = nutrientAccum[foodDate][nutrientKey] ? nutrientAccum[foodDate][nutrientKey] : 0 + nutrientObj.value
						}
					})
					return nutrientAccum
				} catch (error) {
					console.error(error)
					return nutrientAccum
				}
			}, {})
			// making an accumulator object for average macronutrients and settin default total and days 0
			let avgMacronutrient = MACRONUTRIENTS.reduce((keyAccum, nutrientKey) => {
				keyAccum[nutrientKey] = { average: 0, days: 0}
				return keyAccum
			}, {})
			return  Object.values(nutrientMap).reduce((avgAccum, nutrient) => {
				try {
					MACRONUTRIENTS.forEach((nutrientKey) => {
						if(nutrient.hasOwnProperty(nutrientKey)) {
							const beforeAvg = avgAccum[nutrientKey].average
							avgAccum[nutrientKey].average = ( beforeAvg * avgAccum[nutrientKey].days + nutrient[nutrientKey] ) /  (avgAccum[nutrientKey].days + 1)
							avgAccum[nutrientKey].days += 1
						}
					})
					return avgAccum
				} catch (error) {
					console.error(error)
					return avgAccum
				}
			}, avgMacronutrient)
		},
	}
}
